/**
 * 新建调整图层的菜单动作（M3-06 / DESIGN.md §6.5）。
 *
 * 三种调整都以**恒等参数**创建——新建时画面不变，用户在属性面板里调。
 * 创建是普通结构命令，可 undo；新建即活动（属性面板随即显示它）。
 */
import type { ActionRegistry } from './registry';
import type { AppStore } from '../store';
import { createAddLayerCommand } from '../commands/layer-commands';
import { layerDefaults } from '../../document/draft';
import type { Adjustment, AdjustmentLayer } from '../../document/types';
import { layerId } from '../../engine/tile/key';
import { coolToneLut3d, warmToneLut3d, type Lut3D } from '../../engine/adjustments/lut3d';
import { DEFAULT_BASIC_PARAMS } from '../../engine/adjustments/basic';
import { DEFAULT_BLACK_AND_WHITE_PARAMS } from '../../engine/adjustments/blackwhite';
import { DEFAULT_PHOTO_FILTER_PARAMS } from '../../engine/adjustments/photofilter';
import { DEFAULT_CHANNEL_MIXER_PARAMS } from '../../engine/adjustments/channelmixer';
import { DEFAULT_GRADIENT_MAP_PARAMS } from '../../engine/adjustments/gradientmap';
import { DEFAULT_POSTERIZE_PARAMS } from '../../engine/adjustments/posterize';
import { DEFAULT_THRESHOLD_PARAMS } from '../../engine/adjustments/threshold';
import { DEFAULT_VIBRANCE_PARAMS } from '../../engine/adjustments/vibrance';
import { DEFAULT_COLOR_BALANCE_PARAMS } from '../../engine/adjustments/colorbalance';
import { DEFAULT_BRIGHTNESS_CONTRAST_PARAMS } from '../../engine/adjustments/brightnesscontrast';
import { parseCubeFile } from '../../io/lut/cube-parser';
import type { FileGateway } from '../../io/gateway';
import { isAbortError } from '../../io/gateway';

export interface AdjustmentActionDeps {
  readonly store: AppStore;
  /** id 生成器，注入以便测试确定。 */
  readonly nextLayerId: () => string;
  /** .cube 导入用的文件访问，注入以便测试用假实现。 */
  readonly gateway: FileGateway;
}

/** `Lut3D` → `Adjustment.params` 的展开形式（M4-03）。 */
function lutToAdjustment(lut: Lut3D): Adjustment {
  return {
    kind: 'lut',
    params: {
      size: lut.size,
      data: Array.from(lut.data),
      domainMin: [...lut.domainMin],
      domainMax: [...lut.domainMax],
    },
  };
}

const PRESETS: ReadonlyArray<{
  readonly action: string;
  readonly title: string;
  readonly name: string;
  readonly adjustment: Adjustment;
}> = [
  {
    action: 'layer.newCurves',
    title: '曲线…',
    name: '曲线',
    // 三个点：两端 + 可拖的中点，恒等映射
    adjustment: { kind: 'curves', params: { points: [0, 0, 128, 128, 255, 255] } },
  },
  {
    action: 'layer.newLevels',
    title: '色阶…',
    name: '色阶',
    adjustment: {
      kind: 'levels',
      params: { inBlack: 0, inWhite: 255, gamma: 1, outBlack: 0, outWhite: 255 },
    },
  },
  {
    action: 'layer.newHsl',
    title: '色相/饱和度…',
    name: '色相/饱和度',
    adjustment: { kind: 'hsl', params: { hue: 0, saturation: 0, lightness: 0 } },
  },
  {
    action: 'layer.newLutCool',
    title: '颜色查找：冷色调',
    name: '颜色查找（冷色调）',
    adjustment: lutToAdjustment(coolToneLut3d()),
  },
  {
    action: 'layer.newLutWarm',
    title: '颜色查找：暖色调',
    name: '颜色查找（暖色调）',
    adjustment: lutToAdjustment(warmToneLut3d()),
  },
  {
    action: 'layer.newBasic',
    title: '基础调整…',
    name: '基础调整',
    // 恒等参数：七项全 0，新建时画面不变（同其余预设的惯例）
    adjustment: { kind: 'basic', params: { ...DEFAULT_BASIC_PARAMS } },
  },
  {
    action: 'layer.newBlackAndWhite',
    title: '黑白…',
    name: '黑白',
    adjustment: { kind: 'blackAndWhite', params: { ...DEFAULT_BLACK_AND_WHITE_PARAMS } },
  },
  {
    action: 'layer.newPhotoFilter',
    title: '照片滤镜…',
    name: '照片滤镜',
    // 恒等参数：浓度 0，滤镜颜色对结果无影响
    adjustment: { kind: 'photoFilter', params: { ...DEFAULT_PHOTO_FILTER_PARAMS } },
  },
  {
    action: 'layer.newChannelMixer',
    title: '通道混合器…',
    name: '通道混合器',
    adjustment: { kind: 'channelMixer', params: { ...DEFAULT_CHANNEL_MIXER_PARAMS } },
  },
  {
    action: 'layer.newGradientMap',
    title: '渐变映射…',
    name: '渐变映射',
    // 注意：这个调整没有"画面不变"的恒等参数——渐变映射的核心语义就是
    // 按亮度替换颜色，真实 Photoshop 新建时同样立刻转成灰阶渐变
    adjustment: {
      kind: 'gradientMap',
      params: { color1: DEFAULT_GRADIENT_MAP_PARAMS.color1, color2: DEFAULT_GRADIENT_MAP_PARAMS.color2 },
    },
  },
  {
    action: 'layer.newPosterize',
    title: '色调分离…',
    name: '色调分离',
    // 恒等参数：256 级（比 Photoshop 面板上限 255 多留一档，换来严格恒等）
    adjustment: { kind: 'posterize', params: { ...DEFAULT_POSTERIZE_PARAMS } },
  },
  {
    action: 'layer.newThreshold',
    title: '阈值…',
    name: '阈值',
    // 同渐变映射：没有恒等参数，新建时立刻二值化，跟真实 Photoshop 一致
    adjustment: { kind: 'threshold', params: { ...DEFAULT_THRESHOLD_PARAMS } },
  },
  {
    action: 'layer.newVibrance',
    title: '自然饱和度…',
    name: '自然饱和度',
    adjustment: { kind: 'vibrance', params: { ...DEFAULT_VIBRANCE_PARAMS } },
  },
  {
    action: 'layer.newColorBalance',
    title: '颜色平衡…',
    name: '颜色平衡',
    adjustment: { kind: 'colorBalance', params: { ...DEFAULT_COLOR_BALANCE_PARAMS } },
  },
  {
    action: 'layer.newBrightnessContrast',
    title: '亮度/对比度…',
    name: '亮度/对比度',
    adjustment: { kind: 'brightnessContrast', params: { ...DEFAULT_BRIGHTNESS_CONTRAST_PARAMS } },
  },
];

export function registerAdjustmentActions(
  registry: ActionRegistry,
  deps: AdjustmentActionDeps,
): void {
  for (const [order, preset] of PRESETS.entries()) {
    registry.register({
      id: preset.action,
      title: preset.title,
      group: '图层',
      section: 2,
      order,
      enabled: (s) => s.hasDocument,
      run: (services) => {
        const document = services.document();
        if (document === null) return;
        const id = layerId(deps.nextLayerId());
        const layer: AdjustmentLayer = {
          ...layerDefaults(id, preset.name),
          kind: 'adjustment',
          adjustment: preset.adjustment,
        };
        services.dispatch(
          createAddLayerCommand({ layer, parentId: null, index: document.root.children.length }),
        );
        deps.store.setActiveLayer(id);
      },
    });
  }

  // .cube 导入：读文件→解析→建调整图层，三步都可能失败，取消是正常路径不是错误
  registry.register({
    id: 'layer.newLutFromCube',
    title: '颜色查找：从 .cube 导入…',
    group: '图层',
    section: 2,
    order: PRESETS.length,
    enabled: (s) => s.hasDocument,
    run: async (services) => {
      const document = services.document();
      if (document === null) return;
      let opened;
      try {
        opened = await deps.gateway.open(['.cube']);
      } catch (error) {
        if (isAbortError(error)) return;
        throw error;
      }
      const file = opened[0];
      if (file === undefined) return; // 用户取消
      const text = new TextDecoder('utf-8').decode(file.bytes);
      let parsed;
      try {
        parsed = parseCubeFile(text);
      } catch (error) {
        // 畸形 .cube：不吞掉（CLAUDE.md 规则 10），但要有用户可见的反馈而不是控制台里的 unhandled rejection
        deps.store.setHint(`.cube 解析失败：${error instanceof Error ? error.message : String(error)}`);
        return;
      }
      const id = layerId(deps.nextLayerId());
      const layer: AdjustmentLayer = {
        ...layerDefaults(id, `颜色查找（${file.name}）`),
        kind: 'adjustment',
        adjustment: lutToAdjustment(parsed),
      };
      services.dispatch(
        createAddLayerCommand({ layer, parentId: null, index: document.root.children.length }),
      );
      deps.store.setActiveLayer(id);
    },
  });
}
